/**
 * VideoCallsPage.tsx
 *
 * Account-wide list of every Twilio video call (the "Video Calls" nav section).
 * Same recordings/transcripts shown per-patient in the Communication Center comm
 * log, surfaced in one Tasks-style table (Date/Time · Patient · Transcript).
 * Clicking a row opens VideoCallDetailModal (video + scrollable transcript).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { VideoCallDetailModal } from '@/components/VideoCallDetailModal';
import { usePageContext } from '@/hooks/usePageContext';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { extractSearch } from '@/lib/filterRegistry';
import { isDateRange, type FilterConfig } from '@/types/filters.types';
import * as videoCallsSvc from '@/services/videoCalls.service';
import type { VideoCallListItem } from '@/services/videoCalls.service';

const VIDEO_CALL_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search patients...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
];

const PER_PAGE = 25;

function formatDateTime(value: string | null | undefined): string {
  if (!value || value.startsWith('0000')) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function transcriptPreview(item: VideoCallListItem): string {
  const t = item.transcript.trim();
  if (t) return t.replace(/\s+/g, ' ');
  if (item.compositionStatus === 'available') return 'Transcript is being generated…';
  return '—';
}

export function VideoCallsPage() {
  const updateAIContext = usePageContext('Video Calls');
  const { values, negations, setFilter, setNegation, clearFilter } = useLocalDashboardFilters(
    VIDEO_CALL_FILTER_CONFIGS,
    'videoCalls',
  );
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<VideoCallListItem | null>(null);

  const params = useMemo(() => {
    const search = extractSearch(values);
    const dr = values['daterange'];
    const from = isDateRange(dr) ? dr.from : '';
    const to = isDateRange(dr) ? dr.to : '';
    return {
      search: search || undefined,
      dateFrom: from || undefined,
      dateTo: to || undefined,
    };
  }, [values]);

  // Reset to the first page whenever the filters change.
  useEffect(() => {
    setPage(1);
  }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ['video-calls', page, params],
    queryFn: ({ signal }) =>
      videoCallsSvc.listVideoCalls({ page, perPage: PER_PAGE, ...params }, { signal }),
    staleTime: 30_000,
  });

  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  useEffect(() => {
    const totalPages = Math.ceil(total / PER_PAGE) || 1;
    updateAIContext({
      dataSummary: `${total} video call${total !== 1 ? 's' : ''} total, page ${page} of ${totalPages}`,
    });
  }, [total, page, updateAIContext]);

  const columns: DataTableColumn<VideoCallListItem>[] = [
    {
      key: 'createDate',
      header: 'Date / Time',
      width: 'w-48',
      render: (r) => (
        <span className="whitespace-nowrap text-sm text-slate-600">{formatDateTime(r.createDate)}</span>
      ),
    },
    {
      key: 'patientName',
      header: 'Patient',
      width: 'w-56',
      render: (r) => {
        const name = r.patientName?.trim();
        if (!name || !r.patientID || r.patientID === '0') {
          return <span className="text-sm text-slate-400">{name || 'Unknown patient'}</span>;
        }
        return (
          <Link
            to={`/patients/${r.patientID}`}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {name}
          </Link>
        );
      },
    },
    {
      key: 'durationFormatted',
      header: 'Duration',
      width: 'w-32',
      render: (r) => <span className="text-sm text-slate-600">{r.durationFormatted || '—'}</span>,
    },
    {
      key: 'transcript',
      header: 'Transcript',
      truncate: true,
      render: (r) => <span className="text-sm text-slate-600">{transcriptPreview(r)}</span>,
    },
  ];

  return (
    <>
      <ListPageLayout
        filterSidebar={
          <FilterSidebar
            filters={VIDEO_CALL_FILTER_CONFIGS}
            values={values}
            options={{}}
            negations={negations}
            onChange={setFilter}
            onNegateChange={setNegation}
            onClear={clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="videoCalls"
          />
        }
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 p-6 pb-0">
            <PageHeader variant="compact" title="Video Calls" />
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<VideoCallListItem>
              data={items}
              columns={columns}
              keyField="videoCallID"
              loading={isLoading}
              page={page}
              pageSize={PER_PAGE}
              total={total}
              onPageChange={setPage}
              onRowClick={setSelected}
              tableFixed
              emptyTitle="No video calls found"
              emptyDescription="No video calls match the current filters."
            />
          </div>
        </div>
      </ListPageLayout>

      {selected && (
        <VideoCallDetailModal videoCall={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

export default VideoCallsPage;
